package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.AddStaffRequest
import se.mittpass.shared.api.StaffMemberDto
import se.mittpass.shared.api.UpdateStaffRequest
import se.mittpass.shared.api.UpdateStaffSalaryRequest
import se.mittpass.shared.api.CustomRoleDto
import se.mittpass.shared.util.normalizePersonnummer

private val scope = MainScope()

@Composable
fun StaffManagementPage(workplaceId: Long) {
    var staff by remember { mutableStateOf<List<StaffMemberDto>>(emptyList()) }
    var allRoles by remember { mutableStateOf<List<CustomRoleDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var showAddModal by remember { mutableStateOf(false) }
    var showRoleModal by remember { mutableStateOf(false) }
    var editingMember by remember { mutableStateOf<StaffMemberDto?>(null) }
    var editingSalaryMember by remember { mutableStateOf<StaffMemberDto?>(null) }

    LaunchedEffect(workplaceId) {
        staffRepo.getStaff(workplaceId).onSuccess { staff = it }
        customRoleRepo.getRoles(workplaceId).onSuccess { allRoles = it }
        loading = false
    }

    fun refresh() {
        scope.launch {
            staffRepo.getStaff(workplaceId).onSuccess { staff = it }
            customRoleRepo.getRoles(workplaceId).onSuccess { allRoles = it }
        }
    }

    // Header
    Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("margin-bottom", "1rem") } }) {
        Span({ style { property("font-size", "0.7rem"); property("font-weight", "700"); property("color", "var(--rp-muted)"); property("text-transform", "uppercase"); property("letter-spacing", "0.7px"); property("padding", "0 0.25rem") } }) {
            Text("Personal — ${staff.count { it.active }} aktiva")
        }
        Div({ style { property("display", "flex"); property("gap", "0.5rem") } }) {
            Button({
                onClick { showRoleModal = true }
                style {
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                    property("padding", "0.5rem 1rem"); property("border", "1.5px solid var(--rp-pine)")
                    property("background", "transparent"); property("color", "var(--rp-pine)")
                    property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.82rem"); property("cursor", "pointer")
                }
            }) {
                FaIcon("fa-solid fa-tags", size = "0.75rem")
                Text(" Hantera roller")
            }
            Button({
                onClick { showAddModal = true }
                style {
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                    property("padding", "0.5rem 1rem"); property("border", "none")
                    property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color", "white")
                    property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.82rem"); property("cursor", "pointer")
                }
            }) {
                FaIcon("fa-solid fa-user-plus", size = "0.75rem")
                Text(" Lägg till personal")
            }
        }
    }

    // Google Play App download hint banner for staff
    Div({
        style {
            property("background", "var(--rp-surface)")
            property("border", "1px solid var(--rp-hl-med)")
            property("border-left", "4px solid var(--rp-pine)")
            property("border-radius", "16px")
            property("padding", "1rem 1.25rem")
            property("margin-bottom", "1.25rem")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "space-between")
            property("gap", "1rem")
            property("flex-wrap", "wrap")
        }
    }) {
        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.85rem") } }) {
            Div({
                style {
                    property("width", "38px")
                    property("height", "38px")
                    property("border-radius", "10px")
                    property("background", "var(--rp-overlay)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("color", "var(--rp-pine)")
                }
            }) {
                FaIcon("fa-brands fa-google-play", size = "1.2rem", color = "var(--rp-pine)")
            }
            Div {
                Span({ style { property("display", "block"); property("font-weight", "800"); property("font-size", "0.95rem"); property("color", "var(--rp-text)") } }) {
                    Text("Mittpass mobilapp finns på Google Play")
                }
                Span({ style { property("display", "block"); property("font-size", "0.82rem"); property("color", "var(--rp-subtle)") } }) {
                    Text("Dina anställda kan ladda ner appen för stämpling, personalliggare och schema i fickan.")
                }
            }
        }
        A(href = "https://play.google.com/store/apps/details?id=se.mittpass.app&hl=en", attrs = {
            target(ATarget.Blank)
            style {
                property("background", "var(--rp-pine)")
                property("color", "#FFFFFF")
                property("font-size", "0.82rem")
                property("font-weight", "700")
                property("padding", "0.5rem 1rem")
                property("border-radius", "50px")
                property("text-decoration", "none")
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "0.45rem")
            }
        }) {
            FaIcon("fa-brands fa-google-play", size = "0.85rem", color = "var(--rp-surface)")
            Text("Öppna på Google Play")
        }
    }

    if (loading) {
        Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-subtle)") } }) { Text("Laddar…") }
        return
    }
    if (staff.isEmpty()) {
        Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-muted)") } }) { Text("Inga anställda tillagda ännu.") }
        return
    }

    // Separate active and inactive
    val active = staff.filter { it.active }.sortedBy { listOfNotNull(it.firstName, it.lastName).joinToString(" ").ifEmpty { it.email } }
    val inactive = staff.filter { !it.active }.sortedBy { listOfNotNull(it.firstName, it.lastName).joinToString(" ").ifEmpty { it.email } }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.5rem") } }) {
        active.forEach { member -> StaffCard(member, workplaceId, ::refresh, { editingMember = it }, { editingSalaryMember = it }) }

        if (inactive.isNotEmpty()) {
            Div({ style { property("margin-top", "0.5rem") } }) {
                Span({ style { property("font-size", "0.65rem"); property("font-weight", "700"); property("color", "var(--rp-hl-med)"); property("text-transform", "uppercase"); property("letter-spacing", "0.7px"); property("padding", "0 0.25rem") } }) {
                    Text("Inaktiva (${inactive.size})")
                }
            }
            inactive.forEach { member -> StaffCard(member, workplaceId, ::refresh, { editingMember = it }, { editingSalaryMember = it }) }
        }
    }

    // Modals
    if (showAddModal) {
        val usedColors = staff.mapNotNull { it.color }.toSet()
        val defaultPick = DEFAULT_PALETTE_COLORS.firstOrNull { it !in usedColors } ?: DEFAULT_PALETTE_COLORS.first()
        StaffFormModal(
            title = "Lägg till personal",
            confirmLabel = "Lägg till",
            availableRoles = allRoles,
            initialColor = defaultPick,
            onConfirm = { fn, ln, pn, phone, email, selectedRoleIds, color ->
                val addResult = staffRepo.addStaff(workplaceId, AddStaffRequest(fn, ln, pn, phone, email, color = color))
                addResult.map { newUserId ->
                    if (selectedRoleIds.isNotEmpty()) {
                        customRoleRepo.assignRoles(workplaceId, newUserId, selectedRoleIds)
                    }
                    refresh()
                    showAddModal = false
                }
            },
            onCancel = { showAddModal = false }
        )
    }

    editingMember?.let { member ->
        val currentScheme = getEmployeeColorScheme(member.color, member.userId)
        StaffFormModal(
            title = "Redigera personal",
            confirmLabel = "Spara",
            initialFirstName = member.firstName ?: "",
            initialLastName = member.lastName ?: "",
            initialPersonalNumber = member.personalNumber ?: "",
            initialPhone = member.phone ?: "",
            initialEmail = member.email,
            emailReadOnly = true,
            availableRoles = allRoles,
            initialSelectedRoleIds = member.customRoles.map { it.id },
            initialColor = member.color ?: currentScheme.primary,
            onConfirm = { fn, ln, pn, phone, _, selectedRoleIds, color ->
                val updResult = staffRepo.updateStaff(workplaceId, member.userId, UpdateStaffRequest(fn, ln, pn, phone, color = color))
                updResult.map {
                    if (selectedRoleIds != member.customRoles.map { it.id }) {
                        customRoleRepo.assignRoles(workplaceId, member.userId, selectedRoleIds)
                    }
                    refresh()
                    editingMember = null
                }
            },
            onCancel = { editingMember = null }
        )
    }

    editingSalaryMember?.let { member ->
        SalaryModal(
            member = member,
            onSave = { req ->
                scope.launch {
                    staffRepo.updateSalary(workplaceId, member.userId, req)
                    refresh()
                    editingSalaryMember = null
                }
            },
            onCancel = { editingSalaryMember = null }
        )
    }

    if (showRoleModal) {
        RoleManagementModal(
            workplaceId = workplaceId,
            roles = allRoles,
            onRefresh = { refresh() },
            onCancel = { showRoleModal = false }
        )
    }
}

@Composable
private fun RoleManagementModal(
    workplaceId: Long,
    roles: List<CustomRoleDto>,
    onRefresh: () -> Unit,
    onCancel: () -> Unit
) {
    var newRoleName by remember { mutableStateOf("") }
    Div({
        onClick { onCancel() }
        style {
            property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
            property("background", "rgba(13,13,24,0.45)"); property("backdrop-filter", "blur(4px)")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "2rem")
                property("width", "100%"); property("max-width", "400px")
                property("box-shadow", "0 24px 80px rgba(13,13,24,0.18)")
            }
        }) {
            H3({ style { property("margin", "0 0 1rem 0"); property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Hantera roller") }

            Div({ style { property("display", "flex"); property("gap", "0.5rem"); property("margin-bottom", "1.5rem") } }) {
                Input(InputType.Text) {
                    value(newRoleName)
                    onInput { newRoleName = it.value }
                    attr("placeholder", "t.ex. Kock, Servis")
                    style {
                        property("flex", "1"); property("padding", "0.6rem 1rem")
                        property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "12px")
                        property("font-size", "0.95rem"); property("outline", "none"); property("box-sizing", "border-box")
                    }
                }
                Button({
                    onClick {
                        if (newRoleName.isNotBlank()) scope.launch {
                            customRoleRepo.createRole(workplaceId, newRoleName)
                            newRoleName = ""
                            onRefresh()
                        }
                    }
                    style {
                        property("padding", "0.6rem 1.25rem"); property("border", "none")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color", "white")
                        property("border-radius", "12px"); property("font-weight", "700"); property("cursor", "pointer")
                    }
                }) { Text("Lägg till") }
            }

            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.5rem"); property("max-height", "250px"); property("overflow-y", "auto"); property("margin-bottom", "1.5rem") } }) {
                if (roles.isEmpty()) {
                    P({ style { property("color", "var(--rp-muted)"); property("font-size", "0.85rem"); property("text-align", "center") } }) { Text("Inga roller tillagda än.") }
                }
                roles.forEach { r ->
                    Div({ style {
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
                        property("padding", "0.6rem 0.8rem"); property("background", "var(--rp-overlay)"); property("border-radius", "10px")
                    } }) {
                        Span({ style { property("font-weight", "600"); property("font-size", "0.88rem") } }) { Text(r.name) }
                        Button({
                            onClick {
                                scope.launch {
                                    customRoleRepo.deleteRole(workplaceId, r.id)
                                    onRefresh()
                                }
                            }
                            style {
                                property("background", "none"); property("border", "none"); property("color", "var(--rp-love)"); property("cursor", "pointer")
                            }
                        }) { FaIcon("fa-solid fa-trash-can", size = "0.8rem") }
                    }
                }
            }

            Div({ style { property("display", "flex"); property("justify-content", "flex-end") } }) {
                Button({
                    onClick { onCancel() }
                    style { property("padding", "0.6rem 1.5rem"); property("border", "2px solid var(--rp-hl-med)"); property("background", "transparent"); property("color", "var(--rp-pine)"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") }
                }) { Text("Stäng") }
            }
        }
    }
}

@Composable
private fun StaffCard(
    member: StaffMemberDto,
    workplaceId: Long,
    onRefresh: () -> Unit,
    onEdit: (StaffMemberDto) -> Unit,
    onSalary: (StaffMemberDto) -> Unit
) {
    val name = listOfNotNull(member.firstName, member.lastName).joinToString(" ").ifEmpty { member.email }
    val initials = name.split(" ").mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")
    val isInactive = !member.active
    val isManager = member.role == "manager"
    val hasSalary = member.hourlyRateOre != null || member.monthlySalaryOre != null
    val notActivated = !member.emailVerified && member.active
    var inviteSent by remember { mutableStateOf(false) }
    val colorScheme = getEmployeeColorScheme(member.color, member.userId)

    Div({
        style {
            property("background", "var(--rp-surface)"); property("border-radius", "16px")
            property("border", "1px solid var(--rp-hl-med)"); property("padding", "0.875rem 1.25rem")
            property("display", "flex"); property("align-items", "center"); property("gap", "1rem")
            property("flex-wrap", "wrap")
            if (isInactive) property("opacity", "0.55")
        }
    }) {
        // Avatar with designated color
        Div({ style {
            property("width", "38px"); property("height", "38px"); property("border-radius", "50%")
            property("background", colorScheme.primary)
            property("display", "flex"); property("flex-shrink", "0")
            property("align-items", "center"); property("justify-content", "center")
            property("font-weight", "800"); property("font-size", "0.85rem")
            property("color", "#FFFFFF")
            property("box-shadow", "0 2px 6px rgba(0,0,0,0.12)")
        } }) { Text(initials.ifEmpty { "?" }) }

        // Name + meta
        Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.2rem"); property("flex", "1"); property("min-width", "140px") } }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("flex-wrap", "wrap") } }) {
                // Color swatch circle
                Div({ style {
                    property("width", "10px"); property("height", "10px"); property("border-radius", "50%")
                    property("background", colorScheme.primary); property("flex-shrink", "0")
                } }) {}
                Span({ style { property("font-weight", "700"); property("font-size", "0.92rem"); property("color", "var(--rp-text)") } }) { Text(name) }
                // Role badge
                Span({ style {
                    property("font-size", "0.62rem"); property("font-weight", "700"); property("padding", "0.1rem 0.45rem")
                    property("border-radius", "20px"); property("text-transform", "uppercase"); property("letter-spacing", "0.4px")
                    property("background", if (isManager) "var(--rp-warn-bg)" else "var(--rp-overlay)")
                    property("color", if (isManager) "var(--rp-warn-text)" else "var(--rp-subtle)")
                } }) { Text(if (isManager) "Chef" else "Personal") }
                // Custom role badges
                member.customRoles.forEach { r ->
                    Span({ style {
                        property("font-size", "0.62rem"); property("font-weight", "700"); property("padding", "0.1rem 0.45rem")
                        property("border-radius", "20px"); property("background", "var(--rp-info-bg)"); property("color", "var(--rp-iris)")
                        property("border", "1px solid var(--rp-info-bg)")
                    } }) { Text(r.name) }
                }
                // Not-yet-activated badge
                if (notActivated) {
                    Span({ style {
                        property("font-size", "0.62rem"); property("font-weight", "700"); property("padding", "0.1rem 0.45rem")
                        property("border-radius", "20px"); property("text-transform", "uppercase"); property("letter-spacing", "0.4px")
                        property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)")
                        property("border", "1px solid var(--rp-warn-border)")
                    } }) { Text("Ej aktiverad") }
                }
            }
            // Contact info
            Div({ style { property("display", "flex"); property("gap", "1rem"); property("flex-wrap", "wrap") } }) {
                if (!member.email.isNullOrBlank()) {
                    Span({ style { property("font-size", "0.75rem"); property("color", "var(--rp-muted)") } }) { Text(member.email) }
                }
                if (!member.phone.isNullOrBlank()) {
                    Span({ style { property("font-size", "0.75rem"); property("color", "var(--rp-muted)") } }) { Text(member.phone!!) }
                }
                if (!member.personalNumber.isNullOrBlank()) {
                    Span({ style { property("font-size", "0.75rem"); property("color", "var(--rp-hl-med)") } }) { Text(member.personalNumber!!) }
                }
            }
        }

        // Salary info chip (if set)
        if (hasSalary) {
            val salaryLabel = when {
                member.hourlyRateOre != null -> {
                    val ore = member.hourlyRateOre!!
                    val r = if (ore % 100 == 0) "${ore / 100}" else "${ore / 100.0}"
                    "$r kr/h"
                }
                else -> "${(member.monthlySalaryOre ?: 0) / 100} kr/mån"
            }
            Span({ style {
                property("font-size", "0.72rem"); property("font-weight", "700")
                property("color", "var(--rp-pine)"); property("background", "var(--rp-ok-bg)")
                property("padding", "0.25rem 0.6rem"); property("border-radius", "8px")
                property("white-space", "nowrap")
            } }) { Text(salaryLabel) }
        }

        // Actions
        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem"); property("flex-shrink", "0") } }) {

            // Resend invitation (only for non-activated active staff)
            if (notActivated) {
                Button({
                    onClick {
                        if (!inviteSent) scope.launch {
                            staffRepo.resendInvitation(workplaceId, member.userId)
                            inviteSent = true
                        }
                    }
                    attr("title", "Skicka inbjudan igen")
                    style {
                        property("padding", "0.4rem 0.75rem")
                        property("border", "1.5px solid var(--rp-warn-border)")
                        property("background", if (inviteSent) "var(--rp-warn-bg)" else "var(--rp-warn-bg)")
                        property("color", "var(--rp-warn-text)")
                        property("border-radius", "8px"); property("font-weight", "600"); property("font-size", "0.78rem")
                        property("cursor", if (inviteSent) "default" else "pointer")
                        property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem")
                    }
                }) {
                    FaIcon("fa-solid fa-envelope", size = "0.68rem")
                    Text(if (inviteSent) " Skickat!" else " Skicka inbjudan")
                }
            }

            // Edit
            Button({
                onClick { onEdit(member) }
                attr("title", "Redigera uppgifter")
                style {
                    property("padding", "0.4rem 0.75rem"); property("border", "1.5px solid var(--rp-hl-med)")
                    property("background", "transparent"); property("color", "var(--rp-pine)")
                    property("border-radius", "8px"); property("font-weight", "600"); property("font-size", "0.78rem")
                    property("cursor", "pointer"); property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem")
                }
            }) {
                FaIcon("fa-solid fa-pen", size = "0.68rem")
                Text(" Redigera")
            }

            // Salary
            Button({
                onClick { onSalary(member) }
                attr("title", if (hasSalary) "Ändra lön" else "Sätt lön")
                style {
                    property("padding", "0.4rem 0.75rem")
                    property("border", "1.5px solid ${if (hasSalary) "var(--rp-ok-border)" else "var(--rp-hl-med)"}")
                    property("background", if (hasSalary) "var(--rp-ok-bg)" else "transparent")
                    property("color", if (hasSalary) "var(--rp-ok-text)" else "var(--rp-text)")
                    property("border-radius", "8px"); property("font-weight", "600"); property("font-size", "0.78rem")
                    property("cursor", "pointer"); property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem")
                }
            }) {
                FaIcon("fa-solid fa-coins", size = "0.68rem")
                Text(if (hasSalary) " Lön" else " Sätt lön")
            }

            // Toggle active
            Button({
                onClick {
                    scope.launch {
                        staffRepo.setActive(workplaceId, member.userId, !member.active)
                        onRefresh()
                    }
                }
                attr("title", if (isInactive) "Aktivera" else "Inaktivera")
                style {
                    property("padding", "0.4rem 0.65rem")
                    property("border", "none")
                    property("background", if (isInactive) "var(--rp-ok-bg)" else "var(--rp-err-bg)")
                    property("color", if (isInactive) "var(--rp-ok-text)" else "var(--rp-err-text)")
                    property("border-radius", "8px"); property("cursor", "pointer")
                }
            }) {
                FaIcon(if (isInactive) "fa-solid fa-toggle-off" else "fa-solid fa-toggle-on", size = "0.85rem")
            }
        }
    }
}

@Composable
internal fun SalaryModal(
    member: StaffMemberDto,
    onSave: (req: UpdateStaffSalaryRequest) -> Unit,
    onCancel: () -> Unit
) {
    val name = listOfNotNull(member.firstName, member.lastName).joinToString(" ").ifEmpty { member.email }
    var hourlyKr by remember { mutableStateOf(member.hourlyRateOre?.let { if (it % 100 == 0) "${it / 100}" else "${it / 100.0}" } ?: "") }
    var monthlyKr by remember { mutableStateOf(member.monthlySalaryOre?.let { "${it / 100}" } ?: "") }

    var taxTable by remember { mutableStateOf(member.taxTable ?: "") }
    var taxRatePct by remember { mutableStateOf(member.taxRatePct?.toString() ?: "") }

    var obType by remember { mutableStateOf(member.obType) }
    var obEveningVal by remember { mutableStateOf(
        member.obEveningValue?.let { if (member.obType == "fixed_kr") "${it / 100}" else "$it" } ?: ""
    ) }
    var obNightVal by remember { mutableStateOf(
        member.obNightValue?.let { if (member.obType == "fixed_kr") "${it / 100}" else "$it" } ?: ""
    ) }
    var obWeekendVal by remember { mutableStateOf(
        member.obWeekendValue?.let { if (member.obType == "fixed_kr") "${it / 100}" else "$it" } ?: ""
    ) }
    var annualVacDays by remember { mutableStateOf(member.annualVacationDays.toString()) }

    Div({
        onClick { onCancel() }
        style {
            property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
            property("background", "rgba(0,0,0,0.5)"); property("backdrop-filter", "blur(4px)")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "2rem")
                property("width", "100%"); property("max-width", "460px"); property("max-height", "90vh"); property("overflow-y", "auto")
                property("box-shadow", "0 20px 60px rgba(0,0,0,0.15)")
            }
        }) {
            H3({ style { property("margin", "0 0 0.25rem 0"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Lön, Skatt & OB") }
            P({ style { property("margin", "0 0 1.25rem 0"); property("color", "var(--rp-subtle)"); property("font-size", "0.9rem"); property("font-weight", "600") } }) { Text(name) }

            // ── Section 1: Grundlön ──
            Div({ style { property("margin-bottom", "1.25rem"); property("background", "var(--rp-overlay)"); property("padding", "1rem"); property("border-radius", "16px") } }) {
                Span({ style { property("display", "block"); property("font-weight", "800"); property("font-size", "0.85rem"); property("color", "var(--rp-text)"); property("margin-bottom", "0.75rem") } }) {
                    Text("1. Grundlön")
                }
                Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "0.75rem") } }) {
                    Div {
                        Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.72rem"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.25rem") } }) { Text("Timlön (kr/h)") }
                        Input(InputType.Text) {
                            value(hourlyKr)
                            onInput {
                                hourlyKr = it.value
                                if (it.value.replace(",", ".").toDoubleOrNull() != null) monthlyKr = ""
                            }
                            attr("placeholder", "t.ex. 150")
                            style { property("width", "100%"); property("padding", "0.6rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "10px"); property("font-size", "0.9rem"); property("box-sizing", "border-box") }
                        }
                    }
                    Div {
                        Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.72rem"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.25rem") } }) { Text("Månadslön (kr/mån)") }
                        Input(InputType.Text) {
                            value(monthlyKr)
                            onInput {
                                monthlyKr = it.value
                                if (it.value.replace(",", ".").toDoubleOrNull() != null) hourlyKr = ""
                            }
                            attr("placeholder", "t.ex. 30000")
                            style { property("width", "100%"); property("padding", "0.6rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "10px"); property("font-size", "0.9rem"); property("box-sizing", "border-box") }
                        }
                    }
                }
            }

            // ── Section 2: Skatt ──
            Div({ style { property("margin-bottom", "1.25rem"); property("background", "var(--rp-overlay)"); property("padding", "1rem"); property("border-radius", "16px") } }) {
                Span({ style { property("display", "block"); property("font-weight", "800"); property("font-size", "0.85rem"); property("color", "var(--rp-text)"); property("margin-bottom", "0.5rem") } }) {
                    Text("2. Preliminärskatt för lönebesked")
                }
                P({ style { property("font-size", "0.75rem"); property("color", "var(--rp-subtle)"); property("margin", "0 0 0.75rem 0") } }) {
                    Text("Ange antingen skattetabell (t.ex. 32) eller fast skatteprocent. Lämnas tomt används 30% standard.")
                }
                Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "0.75rem") } }) {
                    Div {
                        Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.72rem"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.25rem") } }) { Text("Skattetabell") }
                        Input(InputType.Text) {
                            value(taxTable)
                            onInput {
                                taxTable = it.value
                                if (it.value.isNotBlank()) taxRatePct = ""
                            }
                            attr("placeholder", "t.ex. 32")
                            style { property("width", "100%"); property("padding", "0.6rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "10px"); property("font-size", "0.9rem"); property("box-sizing", "border-box") }
                        }
                    }
                    Div {
                        Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.72rem"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.25rem") } }) { Text("Fast skattesats (%)") }
                        Input(InputType.Text) {
                            value(taxRatePct)
                            onInput {
                                taxRatePct = it.value
                                if (it.value.isNotBlank()) taxTable = ""
                            }
                            attr("placeholder", "30")
                            style { property("width", "100%"); property("padding", "0.6rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "10px"); property("font-size", "0.9rem"); property("box-sizing", "border-box") }
                        }
                    }
                }
            }

            // ── Section 3: OB-ersättning ──
            Div({ style { property("margin-bottom", "1.5rem"); property("background", "var(--rp-overlay)"); property("padding", "1rem"); property("border-radius", "16px") } }) {
                Span({ style { property("display", "block"); property("font-weight", "800"); property("font-size", "0.85rem"); property("color", "var(--rp-text)"); property("margin-bottom", "0.5rem") } }) {
                    Text("3. Individuell OB-ersättning")
                }
                Div({ style { property("display", "flex"); property("gap", "0.5rem"); property("margin-bottom", "0.75rem") } }) {
                    listOf("default" to "Standard", "fixed_kr" to "Fast krona (+kr/h)", "percent" to "Egen procent (%)").forEach { (typeKey, labelStr) ->
                        val isSelected = obType == typeKey
                        Button({
                            onClick { obType = typeKey }
                            style {
                                property("flex", "1"); property("padding", "0.45rem 0.25rem"); property("font-size", "0.72rem"); property("font-weight", "700")
                                property("border", if (isSelected) "2px solid var(--rp-ok-border)" else "1px solid var(--rp-hl-med)")
                                property("background", if (isSelected) "var(--rp-ok-bg)" else "var(--rp-surface)")
                                property("color", if (isSelected) "var(--rp-ok-text)" else "var(--rp-subtle)")
                                property("border-radius", "8px"); property("cursor", "pointer")
                            }
                        }) { Text(labelStr) }
                    }
                }

                if (obType != "default") {
                    val unitLabel = if (obType == "fixed_kr") "kr/h" else "%"
                    Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr 1fr"); property("gap", "0.5rem") } }) {
                        Div {
                            Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.7rem"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.2rem") } }) { Text("Kväll ($unitLabel)") }
                            Input(InputType.Text) {
                                value(obEveningVal)
                                onInput { obEveningVal = it.value }
                                attr("placeholder", if (obType == "fixed_kr") "35" else "25")
                                style { property("width", "100%"); property("padding", "0.5rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "8px"); property("font-size", "0.85rem"); property("box-sizing", "border-box") }
                            }
                        }
                        Div {
                            Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.7rem"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.2rem") } }) { Text("Natt ($unitLabel)") }
                            Input(InputType.Text) {
                                value(obNightVal)
                                onInput { obNightVal = it.value }
                                attr("placeholder", if (obType == "fixed_kr") "50" else "40")
                                style { property("width", "100%"); property("padding", "0.5rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "8px"); property("font-size", "0.85rem"); property("box-sizing", "border-box") }
                            }
                        }
                        Div {
                            Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.7rem"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.2rem") } }) { Text("Helg ($unitLabel)") }
                            Input(InputType.Text) {
                                value(obWeekendVal)
                                onInput { obWeekendVal = it.value }
                                attr("placeholder", if (obType == "fixed_kr") "70" else "50")
                                style { property("width", "100%"); property("padding", "0.5rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "8px"); property("font-size", "0.85rem"); property("box-sizing", "border-box") }
                            }
                        }
                    }
                }
            }

            // ── Section 4: Semesterdagar ──
            Div({ style { property("margin-bottom", "1.5rem"); property("background", "var(--rp-overlay)"); property("padding", "1rem"); property("border-radius", "16px") } }) {
                Span({ style { property("display", "block"); property("font-weight", "800"); property("font-size", "0.85rem"); property("color", "var(--rp-text)"); property("margin-bottom", "0.5rem") } }) {
                    Text("4. Semesterrätt (dagar per år)")
                }
                Div({ style { property("max-width", "200px") } }) {
                    Label(attrs = { style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.72rem"); property("color", "var(--rp-subtle)"); property("margin-bottom", "0.25rem") } }) { Text("Antal semesterdagar") }
                    Input(InputType.Number) {
                        value(annualVacDays)
                        onInput { annualVacDays = it.value?.toString() ?: "" }
                        attr("min", "0")
                        attr("max", "50")
                        attr("placeholder", "25")
                        style { property("width", "100%"); property("padding", "0.6rem"); property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "10px"); property("font-size", "0.9rem"); property("box-sizing", "border-box") }
                    }
                }
            }

            // Buttons
            Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("justify-content", "flex-end") } }) {
                Button({
                    onClick { onCancel() }
                    style { property("padding", "0.7rem 1.25rem"); property("border", "2px solid var(--rp-hl-med)"); property("background", "transparent"); property("color", "var(--rp-pine)"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") }
                }) { Text("Avbryt") }
                Button({
                    onClick {
                        val hourlyOre = hourlyKr.replace(",", ".").toDoubleOrNull()?.let { (it * 100).toInt() }
                        val monthlyOre = monthlyKr.replace(",", ".").toDoubleOrNull()?.let { (it * 100).toInt() }
                        val taxTblVal = taxTable.trim().ifBlank { null }
                        val taxPctVal = taxRatePct.toIntOrNull()

                        val evVal = if (obType == "fixed_kr") obEveningVal.toIntOrNull()?.let { it * 100 } else obEveningVal.toIntOrNull()
                        val niVal = if (obType == "fixed_kr") obNightVal.toIntOrNull()?.let { it * 100 } else obNightVal.toIntOrNull()
                        val weVal = if (obType == "fixed_kr") obWeekendVal.toIntOrNull()?.let { it * 100 } else obWeekendVal.toIntOrNull()
                        val vacDaysVal = annualVacDays.toIntOrNull() ?: 25

                        onSave(
                            UpdateStaffSalaryRequest(
                                hourlyRateOre = hourlyOre,
                                monthlySalaryOre = monthlyOre,
                                taxTable = taxTblVal,
                                taxRatePct = taxPctVal,
                                obType = obType,
                                obEveningValue = evVal,
                                obNightValue = niVal,
                                obWeekendValue = weVal,
                                annualVacationDays = vacDaysVal
                            )
                        )
                    }
                    style { property("padding", "0.7rem 1.75rem"); property("border", "none"); property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))"); property("color", "white"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") }
                }) { Text("Spara ändringar") }
            }
        }
    }
}

@Composable
internal fun StaffFormModal(
    title: String,
    confirmLabel: String,
    initialFirstName: String = "",
    initialLastName: String = "",
    initialPersonalNumber: String = "",
    initialPhone: String = "",
    initialEmail: String = "",
    emailReadOnly: Boolean = false,
    availableRoles: List<CustomRoleDto> = emptyList(),
    initialSelectedRoleIds: List<Long> = emptyList(),
    initialColor: String = DEFAULT_PALETTE_COLORS.first(),
    onConfirm: suspend (firstName: String, lastName: String, personalNumber: String, phone: String, email: String, selectedRoleIds: List<Long>, color: String) -> Result<Unit>,
    onCancel: () -> Unit
) {
    var firstName by remember { mutableStateOf(initialFirstName) }
    var lastName by remember { mutableStateOf(initialLastName) }
    var personalNumber by remember { mutableStateOf(initialPersonalNumber) }
    var phone by remember { mutableStateOf(initialPhone) }
    var email by remember { mutableStateOf(initialEmail) }
    var selectedRoleIds by remember { mutableStateOf(initialSelectedRoleIds) }
    var selectedColor by remember { mutableStateOf(initialColor) }
    var error by remember { mutableStateOf<String?>(null) }
    var isSubmitting by remember { mutableStateOf(false) }

    Div({
        onClick { onCancel() }
        style {
            property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
            property("background", "rgba(13,13,24,0.45)"); property("backdrop-filter", "blur(4px)")
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "2rem 2.25rem")
                property("width", "100%"); property("max-width", "480px")
                property("box-shadow", "0 24px 80px rgba(13,13,24,0.18)")
            }
        }) {
            H3({ style { property("margin", "0 0 1.5rem 0"); property("font-size", "1.25rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text(title) }

            error?.let { err ->
                Div({
                    style {
                        property("background", "var(--rp-err-bg)")
                        property("color", "var(--rp-err-text)")
                        property("padding", "0.75rem 1rem")
                        property("border-radius", "12px")
                        property("margin-bottom", "1rem")
                        property("font-size", "0.85rem")
                        property("font-weight", "600")
                    }
                }) { Text(err) }
            }

            @Composable fun field(label: String, id: String, type: InputType<String>, v: String, ph: String, readOnly: Boolean = false, onV: (String) -> Unit) {
                Div({ style { property("margin-bottom", "1rem") } }) {
                    Label(attrs = {
                        attr("for", id)
                        style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.78rem"); property("color", "var(--rp-text)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem") }
                    }) { Text(label) }
                    Input(type) {
                        id(id); value(v); onInput { onV(it.value); error = null }
                        attr("placeholder", ph)
                        if (readOnly) attr("readonly", "true")
                        style {
                            property("width", "100%"); property("padding", "0.75rem 1rem")
                            property("border", "2px solid var(--rp-hl-med)"); property("border-radius", "12px")
                            property("font-size", "0.95rem"); property("outline", "none")
                            property("box-sizing", "border-box"); property("font-family", "inherit")
                            if (readOnly) { property("background", "var(--rp-overlay)"); property("color", "var(--rp-muted)") }
                        }
                    }
                }
            }

            Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "0 1rem") } }) {
                field("Förnamn", "fn", InputType.Text, firstName, "Anna", onV = { firstName = it })
                field("Efternamn", "ln", InputType.Text, lastName, "Lindqvist", onV = { lastName = it })
            }
            field("Personnummer", "pn", InputType.Text, personalNumber, "ÅÅÅÅMMDD-XXXX eller 10/12 siffror", onV = { personalNumber = it })
            field("Telefon", "ph", InputType.Tel, phone, "070-123 45 67", onV = { phone = it })
            field("E-post", "em", InputType.Email, email, "anna@example.com", readOnly = emailReadOnly, onV = { email = it })

            Div({ style { property("margin-bottom", "1.2rem") } }) {
                Label(attrs = {
                    style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.78rem"); property("color", "var(--rp-text)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.5rem") }
                }) { Text("Personalfärg (visas i schema)") }
                Div({ style { property("display", "flex"); property("flex-wrap", "wrap"); property("gap", "0.6rem"); property("align-items", "center") } }) {
                    DEFAULT_PALETTE_COLORS.forEach { colorHex ->
                        val isSelected = selectedColor.equals(colorHex, ignoreCase = true)
                        Div({
                            onClick { selectedColor = colorHex }
                            style {
                                property("width", "28px"); property("height", "28px")
                                property("border-radius", "50%")
                                property("background", colorHex)
                                property("cursor", "pointer")
                                property("transition", "transform 0.15s ease, box-shadow 0.15s ease")
                                if (isSelected) {
                                    property("box-shadow", "0 0 0 3px white, 0 0 0 5px $colorHex")
                                    property("transform", "scale(1.15)")
                                } else {
                                    property("box-shadow", "0 1px 3px rgba(0,0,0,0.15)")
                                }
                            }
                        })
                    }
                }
            }

            if (availableRoles.isNotEmpty()) {
                Div({ style { property("margin-bottom", "1.2rem") } }) {
                    Label(attrs = {
                        style { property("display", "block"); property("font-weight", "700"); property("font-size", "0.78rem"); property("color", "var(--rp-text)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem") }
                    }) { Text("Roller") }
                    Div({ style { property("display", "flex"); property("flex-wrap", "wrap"); property("gap", "0.4rem") } }) {
                        availableRoles.forEach { r ->
                            val isSelected = r.id in selectedRoleIds
                            Div({
                                onClick {
                                    selectedRoleIds = if (isSelected) selectedRoleIds - r.id else selectedRoleIds + r.id
                                }
                                style {
                                    property("padding", "0.4rem 0.8rem"); property("border-radius", "20px"); property("font-size", "0.78rem"); property("font-weight", "600")
                                    property("cursor", "pointer"); property("user-select", "none")
                                    if (isSelected) {
                                        property("background", "var(--rp-info-bg)"); property("color", "var(--rp-iris)"); property("border", "1.5px solid var(--rp-info-border)")
                                    } else {
                                        property("background", "var(--rp-overlay)"); property("color", "var(--rp-subtle)"); property("border", "1.5px solid var(--rp-hl-med)")
                                    }
                                }
                            }) {
                                Text(r.name)
                            }
                        }
                    }
                }
            }

            Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("justify-content", "flex-end"); property("margin-top", "0.5rem") } }) {
                Button({
                    onClick { onCancel() }
                    if (isSubmitting) disabled()
                    style { property("padding", "0.75rem 1.5rem"); property("border", "2px solid var(--rp-hl-med)"); property("background", "transparent"); property("color", "var(--rp-pine)"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", if (isSubmitting) "not-allowed" else "pointer"); property("font-family", "inherit") }
                }) { Text("Avbryt") }
                Button({
                    onClick {
                        if (firstName.isBlank() || lastName.isBlank() || personalNumber.isBlank() || email.isBlank()) {
                            error = "Alla obligatoriska fält måste fyllas i."
                            return@onClick
                        }
                        val normalizedPn = normalizePersonnummer(personalNumber)
                        if (normalizedPn == null) {
                            error = "Ogiltigt personnummer. Använd formatet YYYYMMDD-XXXX eller 10/12 siffror."
                            return@onClick
                        }
                        isSubmitting = true
                        error = null
                        scope.launch {
                            val res = onConfirm(firstName.trim(), lastName.trim(), normalizedPn, phone.trim(), email.trim(), selectedRoleIds, selectedColor)
                            res.fold(
                                onSuccess = {
                                    isSubmitting = false
                                },
                                onFailure = { ex ->
                                    isSubmitting = false
                                    error = ex.message?.takeIf { it.isNotBlank() } ?: "Ett oväntat fel uppstod."
                                }
                            )
                        }
                    }
                    if (isSubmitting) disabled()
                    style {
                        property("padding", "0.75rem 1.5rem")
                        property("border", "none")
                        property("background", if (isSubmitting) "var(--rp-muted)" else "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                        property("color", "white")
                        property("border-radius", "50px")
                        property("font-weight", "700")
                        property("font-size", "0.95rem")
                        property("cursor", if (isSubmitting) "not-allowed" else "pointer")
                        property("font-family", "inherit")
                    }
                }) { Text(if (isSubmitting) "Sparar…" else confirmLabel) }
            }
        }
    }
}
